// ============================================================================
// ui/designs-menu.js — the rep's saved designs: name, save, version, load, delete
// ============================================================================
// Ryan 2026-07-29: *"I want a comprehensive save menu like we have in the GLD… one where employees
// can name their designs, save, save as a new version, save as a new design, click the '+ New'
// button to create a new design, load their designs, delete them, search them."*
//
// Until this, the tool held exactly ONE design — a single localStorage autosave slot behind
// "Resume your design" — so a rep who built a plate for one school and then another had lost the
// first. This is the GLD's designs drawer (`#designsDrawer` in sections/rep-quote-gym-designer.liquid)
// with the same vocabulary, in WD glass instead of GLD flat-white.
//
// ── REP-ONLY, AND NOT AS A PREFERENCE ────────────────────────────────────────────────────────
// Saving server-side needs a rep JWT (`validateRepCode`), and a storefront buyer has no account.
// So `createDesignsPanel` returns null off the portal, the cluster then never builds the button,
// and the buyer's Save card is untouched — they keep autosave, Resume and share links, which is
// the whole persistence story they can have.
//
// ── ONE PAYLOAD, ONE RESTORE PATH ────────────────────────────────────────────────────────────
// What gets saved is `ctx.shareEnvelope()` — the app's own autosave envelope, the same string
// Resume, share links and the quote handoff carry. Loading calls the same `applyEnvelope` a resume
// does. So a saved design cannot drift from a resumed one, and the worker stays ignorant of what a
// plate is.
// ============================================================================

import { h } from './dom.js';
import { openModal } from './modal.js';
import { isRepMode } from '../rep-mode.js';
import { readDesignIdentity, writeDesignIdentity } from '../design-identity.js';
import { getTray, setTray, serialiseTray, sanitiseTray, trayCount, emptyTray } from './project-tray.js';

import { portalApi as api } from '../portal-api.js';

/** "3 minutes ago" / "yesterday" / "Jul 12" — a list row wants recency, not a timestamp. */
function ago(iso) {
  const then = Date.parse(iso || '');
  if (!Number.isFinite(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  try { return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

/** The row icon for the phone's hamburger list, where there is no cluster button to carry one. */
export const ICON_DESIGNS_ROW =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M4 7a2 2 0 0 1 2-2h3.2l1.6 2H18a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><path d="M4 10h16"/></svg>';

const ICON_TRASH =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
  '<path d="M3 6h18M8 6V4h8v2"/><path d="M5 6l1 14h12l1-14"/><path d="M10 10v7M14 10v7"/></svg>';

/**
 * Build the panel. Returns `{ el, refresh }`, or **null** when this isn't the portal edition.
 *
 * @param {object} ctx the app context — needs `shareEnvelope`, `applyEnvelope`, `status`,
 *   `state`, `el`, and `snapshotPNG` for the list thumbnail.
 */
export function createDesignsPanel(ctx) {
  if (!isRepMode()) return null;

  /* The design currently open, if it came from (or has been saved to) the server. `null` means
     unsaved work — which is why Save has to be able to create as well as update.

     SEEDED FROM STORAGE (Ryan 2026-07-31), because this used to be memory-only and a page reload
     therefore turned a saved design back into "never saved anywhere". The push dialog reads it
     through `getCurrent()`, so every push after a refresh minted a new record and a new WEIGHT-
     ref for work that already had one — three of them in his Lakesong project. See
     design-identity.js for the whole diagnosis and for why this is NOT in the autosave envelope.
       ⚠ BOOT HAS ALREADY DECIDED whether this identity is still valid: `mount()` clears the key
     unless the rep's own autosave actually resumed, so a declined Resume / a shared `?d=` link /
     a fresh browser all reach this line with nothing stored. This panel is built long after that,
     so reading here is reading a verdict, not guessing at one. */
  let current = readDesignIdentity() || { id: null, ref: null, name: '' };
  let index = [];
  let filter = '';

  /* 'loading' | 'ok' | 'error' — see `paintList`. An empty list and a list that never arrived are
     different sentences, and printing the wrong one is how a live design looked deleted. */
  let listState = 'loading';
  let listError = '';

  /* Rows saved this session that the server's scan has not returned yet. `/wd-design-list` is a KV
     `list()`, which is eventually consistent, so a design must not need a round trip to the edge
     before the rep can see they saved it. An entry evicts itself the moment the server has it. */
  const optimistic = new Map();

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — WHICH STACKS ARE OPEN, keyed by chainId
     rather than by position: `paintList` rebuilds every row on each keystroke, save and refresh,
     and the list re-sorts by `updatedAt` the moment anything is saved.

     ⛔ THE PER-ROW SHARE TICKS ARE GONE, and with them `chainSelection`. Ryan: *"perhaps then we
     don't need the checkboxes on the save menu."* Right: the choice now lives in the dialog that
     Copy link, Email my design and Share via put up, which asks at the moment of acting instead of
     in a different panel from the button that reads it. Two places to say one thing is how ticking
     both boxes and pressing Copy link came to copy one. */
  const expanded = new Set();

  const nameInput = h('input', {
    type: 'text', class: 'wd-dm-name', placeholder: 'Untitled design', maxlength: '80',
    autocomplete: 'off', spellcheck: 'false',
  });
  const refBadge = h('span', { class: 'wd-dm-ref', hidden: true });
  const warn = h('p', { class: 'wd-dm-warn', hidden: true }, 'Give this design a name');
  const status = h('p', { class: 'wd-dm-status', role: 'status' });
  const listEl = h('div', { class: 'wd-dm-list' });
  const searchInput = h('input', {
    type: 'text', class: 'wd-dm-search', placeholder: 'Search designs…',
    autocomplete: 'off', spellcheck: 'false',
  });
  const countEl = h('span', { class: 'wd-dm-count' });

  const say = (msg) => { status.textContent = msg || ''; };
  const busy = (on) => { panel.classList.toggle('is-busy', !!on); };

  /* THE ONE PLACE the open design's identity changes — save, save-as, open, "+ New", delete, and
     the push dialog's `adoptSavedDesign` all funnel through here. So persisting here is the whole
     of "the ref survives a reload", and clearing here is the whole of "brand-new work never
     inherits an old ref" (`setCurrent(null)` writes nothing and removes the key). Putting it at
     this single seam is deliberate: an identity written by five callers would eventually be
     written by four. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — "[First Last] - [Organization]", so a save
     is one click rather than a naming decision. Ryan: *"The name should save as '[First Last] -
     [Organization (if there is one)]'."*

     ⚠️ THE ORGANISATION IS OPTIONAL AND ARRIVES LATE. `orgName` is typed on the Customize tab and
     is empty for the first part of most sessions, so the separator is conditional — a bare
     "Ben Rupe - " looks like a bug, and it is the shape you get if you build this with a template
     literal and hope.

     ⚠️ A SUGGESTION, NOT A VALUE. Written only into the empty field of an UNSAVED design: a design
     that has a name has been named, by a person, and overwriting that on every `setCurrent` would
     rename their work under them. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-10-28) — ⛔ NEVER THE REP'S OWN NAME. This read
     `getRep().name`, so every new design offered to save as "Ryan W." — Ryan: *"there's default
     placeholder text in the file name to save it as 'Ryan W.' -- this should be blank."* The person in
     "[First Last] - [Organization]" is the CUSTOMER (the worker names a buyer's design the same way,
     `wdDesignName`), and the designer does not know the customer's name here. So the organisation
     alone, once one has been typed; blank until then. */
  function suggestedName() {
    return String(ctx.state?.designProfile?.orgName || '').trim();
  }

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE CUSTOMER'S DESIGN IS NOT OURS TO
     OVERWRITE. `-A` of an adopted chain is what the buyer actually submitted, and it is the thing a
     customer-facing email calls "the design you made". A plain Save on it, or a "Save as new
     design", would quietly replace that with whatever a rep had just been experimenting with, and
     the label would keep saying it was theirs. Ryan: *"they should be [greyed] so that you have to
     save as a new version and can't accidentally save over the customer version."*

     ⚠️ READ OFF THE LIST, not off a flag on `current`. The list row is the only place `authored`
     arrives, and deriving it here means a rename or a re-adopt cannot leave the buttons disagreeing
     with the badge two rows below them. Unknown (a design saved before adopting existed, or a list
     that has not loaded) reads as OURS — the honest default, and the one that never blocks a rep
     from saving their own work. */
  function isCustomerDesign() {
    if (!current.id) return false;
    const row = index.find((e) => e && e.id === current.id);
    return !!(row && row.authored === 'customer');
  }

  /** Grey the two saves that would REPLACE the open design; leave the one that branches from it. */
  function syncSaveButtons() {
    const locked = isCustomerDesign();
    if (saveBtn) {
      saveBtn.disabled = locked;
      saveBtn.title = locked
        ? 'This is the customer’s own design. Save it as a new version instead.'
        : '';
    }
    if (saveAsNewItem) {
      saveAsNewItem.disabled = locked;
      saveAsNewItem.title = locked
        ? 'This is the customer’s own design. Save it as a new version so the two stay linked.'
        : '';
    }
  }

  function setCurrent(rec) {
    current = { id: rec?.id || null, ref: rec?.ref || null, name: rec?.name || '' };
    nameInput.value = current.name || (isRepMode() ? suggestedName() : '');
    refBadge.textContent = current.ref || '';
    refBadge.hidden = !current.ref;
    writeDesignIdentity(current);
    syncSaveButtons();
  }

  // ── the list ────────────────────────────────────────────────────────────────────────────────
  /* "Customer design" for `-A` of an adopted chain, "Modified Design N" for the revisions after it,
     nothing at all for a design that was never adopted from a share. The number is deliberately off
     by one against the letter: the first MODIFIED design is 1, not 2. */
  /* ⚠️ DERIVED FROM THE LIST, NOT FROM A FIELD. Whether a chain began as a customer's design is
     already knowable: `-A` of it carries `authored: 'customer'` and every row of the chain is in
     the same list. Reading it here beats stamping a second flag onto every revision, which is one
     more thing for `/wd-design-save-version`'s object literal to forget — the mistake that lost
     `linkedDeals` for two months. */
  function adoptedChain(chainId) {
    if (!chainId) return false;
    return index.some((e) => e && e.chainId === chainId && e.authored === 'customer');
  }

  /* Every rung of one chain, oldest first. ⛔ BY REVISION LETTER, not by `updatedAt` — the letter
     is the sequence, the timestamp is just when somebody last touched it, and a re-saved `-A`
     would otherwise sort itself on top of the versions made after it. Same rule, and the same
     reason, as `chainVersions`. */
  function chainRows(chainId) {
    if (!chainId) return [];
    return index
      .filter((e) => e && e.chainId === chainId && e.ref)
      .sort((a, b) => String(a.ref).localeCompare(String(b.ref)));
  }

  function badgeFor(entry) {
    const rev = String(entry.ref || '').split('-').pop() || '';
    if (entry.authored === 'customer') {
      return h('span', { class: 'wd-dm-row__tag wd-dm-row__tag--customer' }, 'Customer design');
    }
    /* A revision of an adopted chain. Without `chainId` there is nothing to be a revision OF, and
       a bare `-B` on a rep's own design is just their second draft, not our version of somebody's. */
    if (!adoptedChain(entry.chainId) || !/^[B-Z]$/.test(rev)) return null;
    /* ⚠️ THE LETTER, matching the Deal Dashboard's card (Ryan 2026-09-02). A rep reading the deal
       and a rep reading the designer must see one vocabulary, and a number that appears nowhere on
       the file was the wrong half of that pair to keep. */
    return h('span', { class: 'wd-dm-row__tag' }, 'Modified version ' + rev);
  }

  function rowFor(entry, opts) {
    const o = opts || {};
    const thumb = h('div', { class: 'wd-dm-row__thumb' });
    if (entry.hasThumbnail) {
      // Lazy, one request per row, and silent on failure — a missing thumbnail must never stop a
      // rep from finding a design by name.
      api(`/wd-design-thumb/${entry.id}`).then((r) => {
        if (r.ok && r.thumbnailDataUrl) thumb.style.backgroundImage = `url("${r.thumbnailDataUrl}")`;
      });
    }
    const del = h('button', {
      type: 'button', class: 'wd-dm-row__del', title: `Delete ${entry.name}`,
      'aria-label': `Delete ${entry.name}`, html: ICON_TRASH,
      onClick: (e) => { e.stopPropagation(); void remove(entry); },
    });
    /* The stack opens and closes from the parent row, and the caret is the only part of that row
       that does not open the design. The count is on it because a collapsed stack must say how
       much it is hiding — a pile with nothing on it reads as a single design. */
    const caret = o.expandable
      ? h('button', {
        type: 'button', class: 'wd-dm-row__caret', title: o.open ? 'Collapse versions' : 'Show versions',
        'aria-label': o.open ? 'Collapse versions' : 'Show versions',
        'aria-expanded': o.open ? 'true' : 'false',
        onClick: (e) => {
          e.stopPropagation();
          if (expanded.has(o.chainId)) expanded.delete(o.chainId); else expanded.add(o.chainId);
          paintList();
        },
      }, h('span', { class: 'wd-dm-row__caret-n' }, String(o.count || 0)), h('span', { class: 'wd-dm-row__caret-v' }, '\u25BE'))
      : null;

    return h('button', {
      type: 'button',
      class: 'wd-dm-row' + (o.child ? ' wd-dm-row--child' : '') + (o.expandable ? ' wd-dm-row--parent' : ''),
      'data-id': entry.id,
      onClick: () => void load(entry),
    },
      thumb,
      h('span', { class: 'wd-dm-row__text' },
        h('span', { class: 'wd-dm-row__name' }, entry.name || 'Untitled design'),
        /* `3 weights` sits between the ref and the age (2026-07-31). A saved design can now be a
           SET — a bumper AND a grip plate AND a dumbbell under one reference number — and a set
           that looks identical in this list to a single plate is a set nobody will trust enough to
           use. Omitted at 0 and on every record saved before sets existed (`pieces` absent), so an
           older design reads exactly as it always has rather than claiming "0 weights". */
        h('span', { class: 'wd-dm-row__meta' }, [
          entry.ref,
          entry.pieces > 0 ? `${entry.pieces} weight${entry.pieces === 1 ? '' : 's'}` : '',
          ago(entry.updatedAt),
        ].filter(Boolean).join(' · ')),
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — WHOSE DESIGN THIS IS, on its own line
           under the date. Ryan: *"with the tag down below on the line that says the date (or just
           below that actually)."*

           ⛔ THE LABEL IS DERIVED FROM THE CHAIN, NOT STORED. `-A` of an adopted chain is the
           customer's; every later revision is one WE made, numbered from one — so `-B` reads
           "Modified Design 1". Storing the words would let them drift from the letters the moment
           a revision is deleted or the chain is rebuilt.

           ⚠️ ONLY ON AN ADOPTED CHAIN. A design a rep started from scratch has no customer version
           to be modified FROM, so it gets no badge at all rather than a misleading "Modified
           Design 1". `authored` is absent on every design saved before this existed, which reads
           as ours — the honest default. */
        badgeFor(entry)),
      caret,
      del,
    );
  }

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — A CHAIN IS ONE PILE, NOT N ROWS. Ryan:
     *"I'm also wondering if the two cards should be stacked like in a pile somehow."* The customer's
     design is the parent and every version we made hangs off it, which is what the data has said
     since WD 377 — `chainId` on every row — and what the flat list could not show: two designs of
     the same artwork sat next to each other with nothing saying one came out of the other.

     ⛔ RENDERING ONLY. No record changes, no new field, no endpoint. The chain is derived from the
     list the panel already holds, so a stack cannot disagree with the designs in it.

     ⚠️ COLLAPSED BY DEFAULT. The pile exists to stop one customer's four designs burying everyone
     else's work in a 34vh list; opening it is a decision, and it is remembered per chain for as
     long as the panel lives. */
  function stackFor(chainId, members, forceOpen) {
    const open = expanded.has(chainId) || !!forceOpen;
    const kids = h('div', { class: 'wd-dm-stack__kids' });
    if (open) for (const m of members.slice(1)) kids.appendChild(rowFor(m, { stack: true, child: true }));

    return h('div', { class: 'wd-dm-stack' + (open ? ' is-open' : '') },
      rowFor(members[0], { stack: true, expandable: true, open, chainId, count: members.length }),
      open ? kids : null,
    );
  }

  function paintList() {
    listEl.innerHTML = '';
    countEl.textContent = index.length ? `${index.length}` : '';

    /* ⚠️ "NO SAVED DESIGNS YET" IS A CLAIM ABOUT THE SERVER, AND ONLY THE 'ok' STATE CAN MAKE IT.
       This used to print it whenever `index` was empty — which includes the moment before the very
       first fetch returns, and every fetch that failed. Ryan 2026-07-30: *"I swear I saved a design
       in the portal yesterday, and today it's no longer in there."* It was there the whole time
       (`wd-design:j5x5z4uw`, untouched); his token had expired, the list call 401'd exactly like
       the save did, and the panel then said the single most alarming thing it could about a design
       that was fine. A UI that cannot load its data must say SO. */
    if (listState === 'loading') {
      listEl.appendChild(h('p', { class: 'wd-dm-empty' }, 'Loading your designs…'));
      return;
    }
    if (listState === 'error') {
      listEl.appendChild(h('p', { class: 'wd-dm-empty' },
        listError || 'Couldn’t load your designs.',
        h('button', {
          type: 'button', class: 'wd-dm-retry wd-flat-glass wd-flat-glass--flush',
          onClick: () => void refresh(),
        }, 'Retry')));
      return;
    }

    const q = filter.trim().toLowerCase();
    const matchesQ = (e) => !q
      || String(e.name || '').toLowerCase().includes(q)
      || String(e.ref || '').toLowerCase().includes(q)
      || String(e.orgName || '').toLowerCase().includes(q);
    const rows = index.filter(matchesQ);
    if (!rows.length) {
      listEl.appendChild(h('p', { class: 'wd-dm-empty' },
        index.length ? 'Nothing matches that.' : 'No saved designs yet.'));
      return;
    }
    /* One pass, and a chain is emitted where its FIRST matching member falls — so the pile keeps
       the place the newest thing in it earned in the `updatedAt` order, rather than jumping to the
       top or sinking to the age of the customer's original.
       ⚠️ The members come from the whole index, not from the filtered `rows`: a search that matches
       one version should show that version in its stack, not tear it out of one. */
    const emitted = new Set();
    for (const e of rows) {
      if (emitted.has(e.id)) continue;
      const members = chainRows(e.chainId);
      if (members.length > 1) {
        members.forEach((m) => emitted.add(m.id));
        /* ⛔ A SEARCH THAT MATCHED A VERSION MUST SHOW THAT VERSION. Stacks are collapsed by
           default, so a match on `-C` alone would otherwise render as its parent `-A` — a row whose
           name does not contain what was typed — and read as the search being broken. Opening for
           the duration of the search is the smallest honest answer; the rep's own expand/collapse
           is untouched underneath and comes back when the box is cleared. */
        const hitChild = !!q && members.slice(1).some(matchesQ);
        listEl.appendChild(stackFor(e.chainId, members, hitChild));
      } else {
        emitted.add(e.id);
        listEl.appendChild(rowFor(e));
      }
    }
  }

  /** Fold in anything saved this session the server hasn't listed yet, and drop the ones it has. */
  function applyOptimistic() {
    for (const [id, row] of optimistic) {
      if (index.some((e) => e && e.id === id)) optimistic.delete(id);
      else index.push(row);
    }
    index.sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  }

  /* ⛔ THE LAST REFRESH ASKED FOR IS THE ONE THAT WINS. Two can be in flight at once — the panel
     loads its list on creation, and adopting a customer's design fires another a moment later — and
     without a token whichever RESPONDS last paints, which is not the same thing. Ryan adopted a
     design that saved perfectly (`wd-design:knNTbprA`, indexed, correctly named) and watched it not
     appear in YOUR DESIGNS: the boot list resolved second and painted the world as it was before
     the adopt. A reload "fixed" it, which is the shape of every race.
       Monotonic, and checked after the await rather than cancelling the fetch: the stale response is
     still perfectly good data, it is just older, and the only thing that must not happen is it
     landing on top of a newer one. */
  let refreshToken = 0;

  async function refresh() {
    const token = ++refreshToken;
    // Only announce loading when there is nothing on screen to keep — a refresh after a save must
    // not blank the list the rep is looking at.
    if (!index.length) { listState = 'loading'; paintList(); }

    const r = await api('/wd-design-list');
    if (token !== refreshToken) return;   // a newer refresh has already been asked for
    if (!r.ok) {
      listState = 'error';
      listError = r.error;
      say(r.error);
      paintList(); // ⚠️ the old code returned here, leaving the false "No saved designs yet." up
      return;
    }
    listState = 'ok';
    listError = '';
    index = Array.isArray(r.designs) ? r.designs : [];
    applyOptimistic();
    paintList();
    /* The list is where `authored` comes from, so the buttons cannot settle until it lands. */
    syncSaveButtons();
  }

  // ── thumbnail ───────────────────────────────────────────────────────────────────────────────
  /** A small deterministic portrait for the list. Small on purpose: it rides inside the save
   *  payload, and the row renders it at 44px. Failure is fine — `hasThumbnail` just stays false. */
  async function thumbDataUrl() {
    try {
      const blob = await ctx.snapshotPNG?.({ size: 420 });
      if (!blob) return null;
      return await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result || ''));
        fr.onerror = () => resolve(null);
        fr.readAsDataURL(blob);
      });
    } catch { return null; }
  }

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — THE PICTURE FOR AN ADOPTED DESIGN.
     `-A` is written by the WORKER from the stored share envelope, and a Worker cannot render a
     plate, so it arrives with no thumbnail and the list shows a blank grey square. The browser can
     render it — just not at the instant the adopt resolves, because the design is still arriving on
     the stage.

     ⚠️ SO IT RETRIES RATHER THAN GUESSING A DELAY. `snapshotPNG` answers null until there is
     something to photograph; three attempts a second apart covers the reveal without pinning this
     to a magic number that a slower machine would miss.

     ⚠️ AND IT FAILS SILENTLY. A missing thumbnail is a blank square, which is exactly what the row
     shows today — so a failure here costs nothing that was not already lost, and is never worth
     interrupting a rep for. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THREE SECONDS WAS NEVER GOING TO BE
     ENOUGH. This ran at ADOPT time, which on a `?d=` link is the same moment presentation mode
     takes the screen — and `snapshotPNG` photographs the reveal, which is busy being a
     presentation. All three attempts fell inside the first three seconds of a set piece the rep
     then watched for half a minute, so every adopted design was born with a blank square and
     nothing ever tried again. `knNTbprA` still read `hasThumbnail: false` days later.

     ⛔ SO IT WAITS FOR THE STAGE, rather than counting attempts against a clock that starts too
     early: it skips while a presentation is running, and gives up only after a minute of a stage
     that never becomes photographable. Cheap while it waits — one boolean per tick.

     ⚠️ AND IT IS NO LONGER A ONE-SHOT. Opening any design that has no thumbnail kicks it again
     (see `openById`), so a blank square heals the next time anybody looks at it instead of needing
     the adopt to be re-run. */
  async function backfillThumb(designId) {
    const deadline = Date.now() + 60000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 1500));
      /* Nothing to photograph yet: the reveal is mid-presentation and `portraitPNG` would either
         refuse or hand back the set piece rather than the plate. */
      if (ctx.isPresenting?.()) continue;
      /* ⛔ AND NO READINESS GUARD. WD 395 added `if (!ctx.stage.ready()) continue;` and the
         thumbnail still never arrived — because `ready()` is `reveal && reveal.isSupported &&
         pipelineMod`, the texture-pipeline module is lazily imported, and a design opened from the
         saved list can sit in that window for the whole minute this loop is willing to wait. The
         two paths that DO produce thumbnails — the save, and the original backfill — ask no such
         question and simply take the picture.
         ⚠️ THE CAPTURE IS ITS OWN READINESS TEST: `thumbDataUrl` returns null when there is nothing
         to photograph, which is exactly what the retry is for. A second gate in front of it could
         only ever be wrong in the direction of never firing. */
      let dataUrl = null;
      try { dataUrl = await thumbDataUrl(); } catch { dataUrl = null; }
      if (!dataUrl) continue;
      try {
        const r = await api('/wd-design-thumb-save', { method: 'POST', body: { designId, thumbnailDataUrl: dataUrl } });
        if (r && r.ok) {
          /* Paint it without a round trip: the row is on screen and the picture is in hand. */
          const row = index.find((e) => e && e.id === designId);
          if (row) row.hasThumbnail = true;
          const el = listEl.querySelector(`.wd-dm-row[data-id="${designId}"] .wd-dm-row__thumb`);
          if (el) el.style.backgroundImage = `url("${dataUrl}")`;
        }
      } catch { /* the design is saved either way */ }
      return;
    }
  }

  // ── save / version / new / load / delete ────────────────────────────────────────────────────
  function meta() {
    const p = ctx.state?.designProfile || {};
    return { product: ctx.state?.ui?.product || '', orgName: p.orgName || '' };
  }

  async function save({ asNew = false, asVersion = false } = {}) {
    const name = nameInput.value.trim();
    if (!name) { warn.hidden = false; nameInput.focus(); return false; }
    warn.hidden = true;
    const design = ctx.shareEnvelope?.();
    if (!design) { say('Nothing to save yet.'); return false; }

    busy(true);
    say(asVersion ? 'Saving a new version…' : 'Saving…');
    const thumbnailDataUrl = await thumbDataUrl();
    /* ── THE SET GOES WITH IT (Ryan 2026-07-31) ────────────────────────────────────────────────
       *"If I'm saving one or 10 designs in the WD, all 1-10 designs should have the same reference
       number as per the saved file."* So a saved design is a SET — a bumper AND a grip plate AND a
       dumbbell — not a single artwork, and every plate in it pushes under this record's one
       WEIGHT- ref.

       The staging tray IS that set already: N artworks, one envelope per artwork fingerprint,
       de-duplicated, with the base-leak guard (project-tray.js). It simply had no home outside
       `localStorage`, so it died with the browser and a rep's mixed-product proposal could not be
       reopened tomorrow. Saving it is the whole feature; no new data model.

       TWO FIELDS, TWO JOBS, and they must not be confused: `design` stays the LIVE STAGE — it is
       what the thumbnail draws, what a share link carries and what the quote handoff posts —
       while `tray` is what a push actually sends. Collapsing them would either put the staged set
       into every share link or lose the stage from the record. */
    const tray = serialiseTray(getTray());
    let r;
    if (asVersion && current.id) {
      r = await api('/wd-design-save-version', { method: 'POST', body: { sourceDesignId: current.id, name, design, tray, thumbnailDataUrl, meta: meta() } });
    } else {
      r = await api('/wd-design-save', { method: 'POST', body: { id: asNew ? undefined : (current.id || undefined), name, design, tray, thumbnailDataUrl, meta: meta() } });
    }
    busy(false);
    if (!r.ok) { say(r.error); return false; }
    setCurrent({ id: r.id, ref: r.ref, name });
    say(`Saved as ${r.ref}.`);
    ctx.status?.success?.(`Saved ${name} (${r.ref}).`);

    const now = new Date().toISOString();
    const m = meta();
    optimistic.set(r.id, {
      id: r.id, name, ref: r.ref, chainId: null,
      product: m.product, orgName: m.orgName,
      // Counted from the tray we just sent, so the optimistic row says the same thing the
      // server's row will say when the list catches up — a count that changes under the rep a
      // second after saving reads as the save having gone wrong.
      pieces: trayCount(getTray()),
      hasThumbnail: !!thumbnailDataUrl, createdAt: now, updatedAt: now,
    });
    await refresh();
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ EVERY SAVE KEEPS THE LINKS CURRENT, not
       just a version save. Ryan tweaked `WEIGHT-1014-C`, saved, opened the snippet link and got the
       drawing as it stood BEFORE the tweak: the sync only ran on save-as-new-version, so an ordinary
       edit left every link already sent showing the previous design.
       ⚠️ `mint: asVersion` is the difference. A version save may CREATE the links, because making a
       version is the act of having something to send. A plain save only refreshes links that already
       exist — a rep saving a design nobody has shared should not have a public URL minted for it as
       a side effect of pressing Save.
       AFTER `refresh`, because the chain is derived from the list and the row just written has to be
       in it. Awaited so a rep who saves and goes straight to the email composer finds it current. */
    if (r.id) await ctx.recordChainShare?.({ mint: asVersion });
    return true;
  }

  async function load(entry) {
    /* Guard the unsaved work that is about to be replaced. `current.id` being null is exactly the
       case worth asking about — nothing on screen has ever been saved anywhere.
         AND THE STAGED SET (2026-07-31). Now that opening a design restores its set, it also
       REPLACES whatever is staged, and staged plates are real work: a colour sweep is a dozen
       primes. Losing them to a dialog that only mentioned "the design on screen" would be exactly
       the silent loss this whole session exists to remove — so the sentence has to name them, and
       the question has to be asked even when the current design IS saved (its own set is safely on
       the server; the plates primed since are not). */
    const staged = trayCount(getTray());
    if ((!current.id && ctx.shareEnvelope?.()) || staged) {
      const lines = [];
      if (!current.id && ctx.shareEnvelope?.()) lines.push('The design on screen hasn’t been saved.');
      if (staged) lines.push(`${staged} staged weight${staged === 1 ? '' : 's'} will be replaced by this design’s own.`);
      const go = await openModal({
        mount: ctx.el,
        title: 'Open a saved design?',
        body: `${lines.join(' ')} Opening this one replaces it.`,
        actions: [{ id: 'go', label: 'Open it', variant: 'primary' }, { id: 'no', label: 'Cancel', variant: 'ghost' }],
      });
      if (go !== 'go') return;
    }
    return openById(entry.id, entry.name);
  }

  /**
   * Open a saved design by id, with NO unsaved-work guard.
   *
   * Split out of `load` (2026-08-30) for the `?designId=` deep link, which the Deal Dashboard's
   * LINKED FILES card needs: a floor plan opens at `/pages/rep-gym-designer?designId=…` and a
   * weight design had no equivalent URL at all, so its card had nowhere to go.
   *
   * ⚠ THE GUARD STAYS IN `load`, DELIBERATELY. It asks about work that is about to be replaced —
   * and on a deep link there is none: the page just booted, nothing has been designed, and the tray
   * is empty. Asking "this will replace your unsaved design" about a blank stage is a dialog that
   * teaches people to dismiss dialogs. Any caller that DOES have work to lose calls `load`.
   */
  /* ⛔ IT ANSWERS WHETHER IT WORKED (Ryan 2026-09-01). This used to `return` bare on both failure
     paths, so a caller could not tell "opened" from "did not open" — it only ever narrated the
     failure to a panel the caller might not be showing. That is fine for a click on a row and
     actively dangerous for `?autosheet=`, which builds a Vendor Design Sheet unattended: a failed
     open would have left the BLANK starting plate on screen and the sheet would have been built,
     registered against the deal, and attached to a purchase order to a factory. The two existing
     callers ignore the value and are unaffected. */
  async function openById(id, name) {
    busy(true); say(`Opening ${name || 'this design'}…`);
    const r = await api(`/wd-design/${id}`);
    busy(false);
    if (!r.ok) { say(r.error); return false; }
    const rec = r.design || {};
    const applied = await ctx.applyEnvelope?.(rec.design);
    if (!applied) { say('That design couldn’t be opened.'); return false; }
    /* ── RESTORE THE SET ────────────────────────────────────────────────────────────────────────
       Both helpers already exist and are already pinned by test/ui-logic.smoke.mjs, which is the
       point of routing through them rather than reading `rec.tray` by hand: `sanitiseTray` ignores
       a payload from another TRAY_VERSION whole, drops an item whose envelope did not survive, and
       re-derives the base map — so a malformed or older set opens EMPTY rather than half-trusted.
         ⚠ AFTER `applyEnvelope`, never before: it clears the identity and would be the natural
       place to clear the tray too if this ever moves. A record saved before sets existed has no
       `tray` at all, and empties the staging area — correct, because that design genuinely stages
       nothing, and leaving the previous customer's plates behind is the fault this replaces. */
    let restored = emptyTray();
    if (rec.tray) { try { restored = sanitiseTray(JSON.parse(rec.tray)); } catch { restored = emptyTray(); } }
    const write = setTray(restored);
    setCurrent(rec);
    say('');
    const n = trayCount(restored);
    const set = n ? ` — ${n} weight${n === 1 ? '' : 's'} staged` : '';
    ctx.status?.success?.(`Opened ${rec.name} (${rec.ref})${set}.`);
    /* The tray's own quota story, said out loud at the one moment it can bite. A set big enough to
       live on the server (6 MB) can be too big for `localStorage` (~5 MB), so the plates are all
       here and will all send — they just won't survive the NEXT reload. Silence here would look
       like the set had loaded fine and then evaporated. */
    if (n && write.memoryOnly) {
      say('This browser ran out of local storage, so these staged weights are held in memory only — they’ll send fine, but won’t survive a page reload.');
    }
    /* ⛔ A BLANK SQUARE HEALS ITSELF. An adopted `-A` is written by the Worker, which cannot render
       a plate, and any design whose first backfill lost its race has stayed blank ever since. The
       design is now on the stage and this browser can photograph it, so ask. Best effort and
       unawaited: it must not delay handing the rep their design. */
    if (!rec.hasThumbnail) void backfillThumb(rec.id);
    return true;
  }

  /**
   * "+ New" — a new CUSTOMER, so a blank plate, a new reference number, and an empty set.
   *
   * ⚠ CLEARING THE TRAY IS DESTRUCTIVE AND THE DIALOG MUST SAY SO. Now that the staged set belongs
   * to the design, leaving it behind would carry one school's plates into the next school's
   * reference number — the mirror-image of the ref bug fixed today, and the worse half, because it
   * is silent and customer-facing. But a rep who has primed three plates and taps "+ New" expecting
   * a fresh plate would lose real work, so the count is named before anything is thrown away.
   *   Note the set is NOT how a rep builds a mixed proposal: same logo, switch PRODUCT, prime
   * again. "+ New" is the other thing entirely, and must not read like it.
   */
  async function startNew() {
    const staged = trayCount(getTray());
    const lines = [
      current.id
        ? `“${current.name}” is saved as ${current.ref} — you can open it again from this menu any time.`
        : 'The design on screen hasn’t been saved. Starting fresh will discard it.',
    ];
    if (staged) {
      lines.push(current.id
        ? `Its ${staged} staged weight${staged === 1 ? '' : 's'} ${staged === 1 ? 'was' : 'were'} saved with it and will be cleared from the staging area.`
        : `${staged} staged weight${staged === 1 ? '' : 's'} will be cleared too.`);
    }
    const go = await openModal({
      mount: ctx.el,
      title: 'Start a new design?',
      body: lines.join(' '),
      actions: [{ id: 'go', label: 'Start new', variant: 'primary' }, { id: 'no', label: 'Cancel', variant: 'ghost' }],
    });
    if (go !== 'go') return;
    setCurrent(null);
    nameInput.value = '';
    say('');
    ctx.startNewDesign?.();
  }

  async function remove(entry) {
    const go = await openModal({
      mount: ctx.el,
      title: `Delete “${entry.name}”?`,
      body: 'It moves to the trash and an admin can restore it for a while — but it leaves your list now.',
      actions: [{ id: 'go', label: 'Delete', variant: 'primary' }, { id: 'no', label: 'Cancel', variant: 'ghost' }],
    });
    if (go !== 'go') return;
    busy(true);
    const r = await api('/wd-design-delete', { method: 'POST', body: { id: entry.id } });
    busy(false);
    if (!r.ok) { say(r.error); return; }
    if (current.id === entry.id) setCurrent(null);
    optimistic.delete(entry.id); // or it would be re-folded back in as "not listed yet"
    index = index.filter((e) => e && e.id !== entry.id);
    say(`Deleted ${entry.name}.`);
    await refresh();
  }

  // ── the split save button ───────────────────────────────────────────────────────────────────
  /* Declared on its own so `syncSaveButtons` can grey it: this is the OTHER save that would replace
     the customer's design rather than branch from it. */
  const saveAsNewItem = h('button', {
    type: 'button', class: 'wd-dm-more__item',
    onClick: () => { moreMenu.hidden = true; void save({ asNew: true }); },
  }, 'Save as new design');
  const moreMenu = h('div', { class: 'wd-dm-more wd-flat-glass', hidden: true },
    h('button', { type: 'button', class: 'wd-dm-more__item', onClick: () => { moreMenu.hidden = true; void save({ asVersion: true }); } },
      'Save as new version'),
    saveAsNewItem,
  );
  const moreBtn = h('button', {
    type: 'button', class: 'wd-dm-split', title: 'More save options', 'aria-label': 'More save options',
    onClick: (e) => { e.stopPropagation(); moreMenu.hidden = !moreMenu.hidden; },
  }, '▾');

  const saveBtn = h('button', { type: 'button', class: 'wd-dm-save', onClick: () => void save() }, 'Save');
  const newBtn = h('button', { type: 'button', class: 'wd-dm-new wd-flat-glass wd-flat-glass--flush', onClick: () => void startNew() }, '+ New');
  // (+ New keeps flat-glass — it IS a button, like the cluster's own — and gains the same hover
  //  lift they have. The LIST ROWS drop it entirely; see .wd-dm-row in the stylesheet.)

  searchInput.addEventListener('input', () => { filter = searchInput.value; paintList(); });
  nameInput.addEventListener('input', () => { if (nameInput.value.trim()) warn.hidden = true; });
  nameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); void save(); } });

  const panel = h('div', { class: 'wd-dm' },
    h('div', { class: 'wd-dm-namerow' },
      nameInput,
      h('div', { class: 'wd-dm-savewrap' }, saveBtn, moreBtn, moreMenu)),
    warn,
    h('div', { class: 'wd-dm-actions' }, newBtn, refBadge),
    h('div', { class: 'wd-dm-listhead' },
      h('span', { class: 'wd-dm-listhead__label' }, 'Your designs'), countEl),
    searchInput,
    listEl,
    status,
  );
  // A click anywhere else in the panel closes the little dropdown — it is a menu, not a mode.
  panel.addEventListener('click', (e) => { if (!moreMenu.contains(e.target) && e.target !== moreBtn) moreMenu.hidden = true; });

  // Paint the seeded identity into the name field + the ref badge. Without this the panel opens
  // blank-named on a resumed design that the app can perfectly well name — and the rep, seeing no
  // ref, would reasonably conclude it had never been saved and save it again.
  setCurrent(current);

  paintList();
  void refresh();

  /* `getCurrent` (2026-07-30) — WHICH saved design is open, for anything outside this panel
     that needs a real `designId`. Its first caller is ui/project-push.js: `/wd-plate-upload`
     namespaces plate PNGs by design and checks ownership against the stored record, so a
     push has to know whether this design has ever been saved — and if it has, reuse that id
     rather than creating a duplicate design every time a rep sends plates to a project.
       A COPY, deliberately: `current` is this panel's own state and a caller that could hold
     a reference to it could also rewrite it, which would desynchronise the name field and
     the ref badge from the design they claim to describe. */
  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — EVERY VERSION OF THE OPEN DESIGN, with its
     envelope and the name a customer should see on that slide. Exposed from here because this panel
     is the only thing that holds the rep's index, and deriving the chain from it means no new
     endpoint: `/wd-design-list` already returns `chainId` and `ref` for every row.

     ⚠️ THE ENVELOPES ARE FETCHED ONE PER VERSION. The list is metadata only — no `design` field, by
     design (a 1 KB metadata ceiling). So this costs one GET per rung, which is why it runs on a
     click and not on every card open.

     ⛔ ORDERED BY REVISION LETTER, not by `updatedAt`. A rep who re-opens and re-saves `-A` after
     making `-B` would otherwise send the customer their own design labelled as the newest thing we
     made. The letter is the sequence; the timestamps are just when somebody last touched it. */
  /* ⚠️ ALWAYS THE WHOLE CHAIN. Narrowing it was the ticks' job and is now the share dialog's, which
     filters the list this returns rather than asking for a filtered one. One caller, one meaning. */
  async function chainVersions() {
    if (!current.id) return [];
    const mine = index.find((e) => e && e.id === current.id);
    const chainId = mine && mine.chainId;
    if (!chainId) return [];
    const rows = chainRows(chainId);
    if (rows.length < 2) return [];

    const out = [];
    for (const row of rows) {
      let rec = null;
      try { const r = await api(`/wd-design/${row.id}`); rec = r && r.ok ? r.design : null; }
      catch { rec = null; }
      if (!rec || !rec.design) continue;
      const rev = String(row.ref).split('-').pop() || '';
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — ⛔ THE FACTS, NOT A SENTENCE. This used
         to emit one `label` that served two audiences at once, and they want opposite words: the
         rep picking versions in the share dialog is looking at the CUSTOMER'S design, while the
         customer opening the link is looking at THEIR OWN. One string could only ever be right for
         one of them, and it was right for the customer, so the dialog told a rep to tick "Your
         original design" when it was not theirs. Ryan: *"the 'Your original design' should read
         'Customers original design'."*
         The customer-facing headline is built at share time instead (`slideLabelFor` in ui/share.js),
         because it also depends on something a single version cannot know: how many versions the
         link ends up carrying. */
      out.push({
        id: row.id,
        design: rec.design,
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-02) — THE LINKS ALREADY HANDED OUT for this
           rung. The chain's link is stable now: it is updated in place as versions are added rather
           than re-minted, so a URL a customer already has keeps working and keeps up. Carried here
           because this loop is already reading each version's full record — the list cannot supply
           them (a 9-field whitelist under KV's 1 KB metadata ceiling). */
        chainShareId: (rec && rec.chainShareId) || '',
        soloShareId: (rec && rec.soloShareId) || '',
        /* The customer's own immutable link, on their rung only. It rides onto the slide as
           `origin` so an edit made from a combined link writes to the submission the rep's Deal
           Dashboard reads, not to the combined link itself. */
        fromShare: (rec && rec.fromShare) || '',
        rev,
        authored: row.authored === 'customer' ? 'customer' : 'rep',
        /* Did the CUSTOMER start this chain? True for every rung of it, including ours — it is a
           fact about the chain, not about the design. */
        adopted: adoptedChain(chainId),
        /* What the REP reads in the share dialog. */
        pickLabel: row.authored === 'customer'
          ? 'Customers original design'
          : (adoptedChain(chainId) && /^[B-Z]$/.test(rev)
            ? `Modified version ${rev}`
            : `Version ${rev}`),
      });
    }
    return out;
  }

  return { el: panel, refresh, setCurrent, getCurrent: () => ({ ...current }), openById, chainVersions, backfillThumb };
}
